/**
 * @file LoadAllButtonContainer.tsx
 * @description Container that binds the tier name into the toggle callback
 * so the view only needs a simple onPress without arguments.
 * @module features/squad-planning/components/LoadAllButton
 */

import React, { memo, useCallback } from "react";

import LoadAllButtonView from "./LoadAllButtonView";

/** Props for {@link LoadAllButtonContainer} */
export interface LoadAllButtonContainerProps {
  /** Name of the tier this button controls */
  readonly tierName: string;
  /** Total number of players in the tier */
  readonly totalCount: number;
  /** Whether the tier is currently showing all players */
  readonly isExpanded: boolean;
  /** Callback to toggle a tier's expanded state by name */
  readonly onToggle: (tierName: string) => void;
}

/**
 * Binds tierName into the toggle callback for the LoadAllButton view.
 * @param props - Container props
 * @param props.tierName - Tier this button controls
 * @param props.totalCount - Total player count for the label
 * @param props.isExpanded - Whether the tier list is fully expanded
 * @param props.onToggle - Parent callback expecting the tier name
 * @returns LoadAllButtonView with a bound onPress
 */
const LoadAllButtonContainer = ({
  tierName,
  totalCount,
  isExpanded,
  onToggle,
}: LoadAllButtonContainerProps) => {
  const handlePress = useCallback(
    () => onToggle(tierName),
    [onToggle, tierName]
  );

  return (
    <LoadAllButtonView
      totalCount={totalCount}
      isExpanded={isExpanded}
      onPress={handlePress}
    />
  );
};

LoadAllButtonContainer.displayName = "LoadAllButtonContainer";

export default memo(LoadAllButtonContainer);
